/*
 *
 * Hedera Stablecoin SDK
 *
 * Copyright (C) 2023 Hedera Hashgraph, LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import axios from 'axios';
import { singleton } from 'tsyringe';
import LogService from '../../../app/service/LogService.js';
import { StableCoinNotFound } from './error/StableCoinNotFound.js';
import BigDecimal from '../../../domain/context/shared/BigDecimal.js';
import { PublicKey as HPublicKey } from '@hashgraph/sdk';
import PublicKey from '../../../domain/context/account/PublicKey.js';
import { StableCoinMemo } from '../../../domain/context/stablecoin/StableCoinMemo.js';
import ContractId from '../../../domain/context/contract/ContractId.js';
import { MAX_PERCENTAGE_DECIMALS } from '../../../domain/context/fee/CustomFee.js';
import { HBAR_DECIMALS } from '../../../core/Constants.js';
import { InvalidResponse } from './error/InvalidResponse.js';
import { HederaId } from '../../../domain/context/shared/HederaId.js';
import { KeyType } from '../../../domain/context/account/KeyProps.js';
import EvmAddress from '../../../domain/context/contract/EvmAddress.js';
import { REGEX_TRANSACTION } from '../error/TransactionResponseError.js';
import MultiKey from '../../../domain/context/account/MultiKey.js';
const PROTOBUF_ENCODED = 'ProtobufEncoded';
let MirrorNodeAdapter = class MirrorNodeAdapter {
    set(mnConfig) {
        this.mirrorNodeConfig = mnConfig;
        this.instance = axios.create({
            validateStatus: function (status) {
                return (status >= 200 && status < 300) || status == 404;
            },
        });
        this.mirrorNodeConfig.baseUrl = mnConfig.baseUrl.endsWith('/')
            ? mnConfig.baseUrl
            : `${mnConfig.baseUrl}/`;
        if (this.mirrorNodeConfig.headerName && this.mirrorNodeConfig.apiKey)
            this.instance.defaults.headers.common[this.mirrorNodeConfig.headerName] = this.mirrorNodeConfig.apiKey;
    }
    async getStableCoinsList(accountId) {
        try {
            const url = `${this.mirrorNodeConfig.baseUrl}tokens?limit=100&order=desc&account.id=${accountId.toString()}`;
            LogService.logTrace('Getting stablecoin list from mirror node -> ', url);
            const resObject = {
                coins: [],
            };
            const res = await this.instance.get(url);
            res.data.tokens.map((item) => {
                resObject.coins.push({
                    id: item.token_id,
                    symbol: item.symbol,
                });
            });
            return resObject;
        }
        catch (error) {
            LogService.logError(error);
            return Promise.reject(new InvalidResponse(error));
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getTokenInfo(tokenId) {
        const url = `${this.mirrorNodeConfig.baseUrl}tokens/${tokenId.toString()}`;
        console.log('Getting token from mirror node -> ', url);
        LogService.logTrace('Getting token from mirror node -> ', url);
        const retry = 10;
        let i = 0;
        let response;
        do {
            if (i > 0)
                await new Promise((resolve) => setTimeout(resolve, 2000));
            response = await this.instance.get(url);
            i++;
        } while (response.status !== 200 && i < retry);
        return response;
    }
    async getStableCoin(tokenId) {
        console.log('Getting stablecoin from mirror node -> ', tokenId.toString());
        try {
            const response = await this.getTokenInfo(tokenId);
            const getKeyOrDefault = (val) => {
                if (val?._type === 'ProtobufEncoded') {
                    return ContractId.fromProtoBufKey(val.key);
                }
                if (val) {
                    return new PublicKey({
                        key: val.key,
                        type: val._type,
                    });
                }
                else {
                    return undefined;
                }
            };
            console.log('CUSTOM FEES' + response.data.custom_fees);
            const getCustomFeesOrDefault = async (val) => {
                if (!val)
                    return undefined;
                const customFees = [];
                for (const fixedFee of val.fixed_fees) {
                    const denominatingToken = fixedFee.denominating_token_id
                        ? HederaId.from(fixedFee.denominating_token_id)
                        : HederaId.NULL;
                    let feeDecimals = decimals;
                    if (denominatingToken.isNull())
                        feeDecimals = HBAR_DECIMALS;
                    else if (denominatingToken.toString() !== tokenId.toString()) {
                        feeDecimals = parseInt((await this.getTokenInfo(denominatingToken)).data
                            .decimals ?? '0');
                    }
                    const requestFixedFee = {
                        tokenIdCollected: fixedFee.denominating_token_id
                            ? fixedFee.denominating_token_id
                            : '0.0.0',
                        amount: BigDecimal.fromStringFixed(fixedFee.amount ? fixedFee.amount.toString() : '0', feeDecimals).toString(),
                        decimals: feeDecimals,
                        collectorId: fixedFee.collector_account_id,
                        collectorsExempt: fixedFee.all_collectors_are_exempt,
                    };
                    customFees.push(requestFixedFee);
                }
                console.log('FRACTIONAL FEES' + val.fractional_fees);
                val.fractional_fees.forEach((fractionalFee) => {
                    const requestFractionalFee = {
                        decimals: decimals,
                        collectorId: fractionalFee.collector_account_id,
                        collectorsExempt: fractionalFee.all_collectors_are_exempt,
                        percentage: BigDecimal.fromStringFixed(Math.round((100 *
                            10 ** MAX_PERCENTAGE_DECIMALS *
                            parseInt(fractionalFee.amount.numerator)) /
                            parseInt(fractionalFee.amount.denominator)).toString(), MAX_PERCENTAGE_DECIMALS).toString(),
                        amountNumerator: fractionalFee.amount.numerator,
                        amountDenominator: fractionalFee.amount.denominator,
                        min: BigDecimal.fromStringFixed(fractionalFee.minimum
                            ? fractionalFee.minimum.toString()
                            : '0', decimals).toString(),
                        max: BigDecimal.fromStringFixed(fractionalFee.maximum
                            ? fractionalFee.maximum.toString()
                            : '0', decimals).toString(),
                        net: fractionalFee.net_of_transfers,
                    };
                    customFees.push(requestFractionalFee);
                });
                return customFees;
            };
            if (response.status !== 200) {
                throw new StableCoinNotFound(tokenId.toString());
            }
            const decimals = parseInt(response.data.decimals ?? '0');
            const transformedMemo = await this.transformTokenMemo(response.data.memo);
            const proxyAddress = response.data.memo
                ? StableCoinMemo.fromJson(JSON.stringify(transformedMemo))
                    .proxyContract
                : '0.0.0';
            const proxyAdminAddress = response.data.memo
                ? StableCoinMemo.fromJson(JSON.stringify(transformedMemo))
                    .proxyAdminContract
                : '0.0.0';
            const proxyAddressContractInfo = await this.getContractInfo(proxyAddress);
            const proxyAdminAddressContractInfo = await this.getContractInfo(proxyAdminAddress);
            const proxyId = proxyAddressContractInfo.id;
            const proxyAdminId = proxyAdminAddressContractInfo.id;
            const stableCoinDetail = {
                tokenId: HederaId.from(response.data.token_id),
                name: response.data.name ?? '',
                symbol: response.data.symbol ?? '',
                decimals: decimals,
                initialSupply: response.data.initial_supply
                    ? BigDecimal.fromStringFixed(response.data.initial_supply, decimals)
                    : BigDecimal.ZERO,
                totalSupply: response.data.total_supply
                    ? BigDecimal.fromStringFixed(response.data.total_supply, decimals)
                    : BigDecimal.ZERO,
                maxSupply: response.data.max_supply
                    ? BigDecimal.fromStringFixed(response.data.max_supply, decimals)
                    : undefined,
                proxyAddress: new ContractId(proxyId),
                proxyAdminAddress: new ContractId(proxyAdminId),
                evmProxyAddress: new EvmAddress(proxyAddressContractInfo.evmAddress),
                evmProxyAdminAddress: new EvmAddress(proxyAdminAddressContractInfo.evmAddress),
                treasury: HederaId.from(response.data.treasury_account_id),
                paused: response.data.pause_status === 'PAUSED',
                deleted: Boolean(response.data.deleted) ?? false,
                freezeDefault: Boolean(response.data.freeze_default) ?? false,
                autoRenewAccount: HederaId.from(response.data.auto_renew_account),
                autoRenewPeriod: response.data.auto_renew_period,
                expirationTimestamp: response.data.expiry_timestamp,
                adminKey: getKeyOrDefault(response.data.admin_key),
                kycKey: getKeyOrDefault(response.data.kyc_key),
                freezeKey: getKeyOrDefault(response.data.freeze_key),
                wipeKey: getKeyOrDefault(response.data.wipe_key),
                supplyKey: getKeyOrDefault(response.data.supply_key),
                pauseKey: getKeyOrDefault(response.data.pause_key),
                feeScheduleKey: getKeyOrDefault(response.data.fee_schedule_key),
                customFees: await getCustomFeesOrDefault(response.data.custom_fees),
            };
            return stableCoinDetail;
        }
        catch (error) {
            LogService.logError(error);
            return Promise.reject(new InvalidResponse(error));
        }
    }
    async transformTokenMemo(memo) {
        const transformedMemo = JSON.parse(memo);
        transformedMemo.p = await this.getHederaIdfromContractAddress(transformedMemo.p);
        transformedMemo.a = await this.getHederaIdfromContractAddress(transformedMemo.a);
        return transformedMemo;
    }
    async getHederaIdfromContractAddress(contractAddress) {
        if (!contractAddress)
            return '';
        if (contractAddress.length >= 40)
            return (await this.getContractInfo(contractAddress)).id;
        return contractAddress;
    }
    async getAccountInfo(accountId) {
        try {
            LogService.logTrace('Getting account info -> ', this.mirrorNodeConfig.baseUrl + 'accounts/' + accountId);
            const res = await this.instance.get(this.mirrorNodeConfig.baseUrl +
                'accounts/' +
                accountId.toString());
            const account = {
                id: res.data.account.toString(),
                accountEvmAddress: res.data.evm_address,
                alias: res.data.alias,
            };
            if (res.data.key) {
                if (res.data.key._type != PROTOBUF_ENCODED) {
                    account.publicKey = new PublicKey({
                        key: res.data.key ? res.data.key.key : undefined,
                        type: res.data.key
                            ? res.data.key._type
                            : undefined,
                    });
                }
                else {
                    account.multiKey = MultiKey.fromProtobuf(res.data.key.key);
                }
            }
            return account;
        }
        catch (error) {
            LogService.logError(error);
            return Promise.reject(new InvalidResponse(error));
        }
    }
    async getContractMemo(contractId) {
        try {
            LogService.logTrace(this.mirrorNodeConfig.baseUrl + 'contracts/' + contractId);
            const res = await this.instance.get(this.mirrorNodeConfig.baseUrl +
                'contracts/' +
                contractId.toString());
            return res.data.memo;
        }
        catch (error) {
            LogService.logError(error);
            return Promise.reject(new InvalidResponse(error));
        }
    }
    async getContractInfo(contractEvmAddress) {
        try {
            const url = `${this.mirrorNodeConfig.baseUrl}contracts/${contractEvmAddress}`;
            LogService.logTrace('Getting contract info -> ', url);
            const retry = 10;
            let i = 0;
            let response;
            do {
                if (i > 0)
                    await new Promise((resolve) => setTimeout(resolve, 2000));
                response = await this.instance.get(url);
                i++;
            } while (response.status !== 200 && i < retry);
            const contract = {
                id: response.data.contract_id,
                evmAddress: response.data.evm_address,
            };
            return contract;
        }
        catch (error) {
            LogService.logError(error);
            return Promise.reject(new InvalidResponse(error));
        }
    }
    async getAccountToken(targetId, tokenId) {
        try {
            const url = `${this.mirrorNodeConfig.baseUrl}accounts/${targetId.toString()}/tokens?token.id=${tokenId.toString()}`;
            LogService.logTrace('Getting account token -> ', url);
            const res = await this.instance.get(url);
            if (res.data.tokens && res.data.tokens.length > 0) {
                const obj = res.data.tokens[0];
                return {
                    automaticAssociation: obj.automatic_association,
                    balance: BigDecimal.fromString(obj.balance.toString()),
                    createdTimestamp: obj.created_timestamp,
                    freezeStatus: obj.freeze_status,
                    kycStatus: obj.kyc_status,
                    tokenId: HederaId.from(obj.token_id),
                };
            }
            else {
                return undefined;
            }
        }
        catch (error) {
            LogService.logError(error);
            return Promise.reject(new InvalidResponse(error));
        }
    }
    async getTransactionResult(transactionId) {
        try {
            const url = this.mirrorNodeConfig.baseUrl +
                'contracts/results/' +
                transactionId;
            LogService.logTrace(url);
            const res = await this.instance.get(url);
            if (!res.data.call_result)
                throw new Error('Response does not contain a transaction result');
            const result = {
                result: res.data.call_result.toString(),
            };
            return result;
        }
        catch (error) {
            LogService.logError(error);
            return Promise.reject(new InvalidResponse(error));
        }
    }
    async getTransactionFinalError(transactionId) {
        try {
            if (transactionId.match(REGEX_TRANSACTION))
                transactionId = transactionId
                    .replace('@', '-')
                    .replace(/.([^.]*)$/, '-$1');
            const url = this.mirrorNodeConfig.baseUrl + 'transactions/' + transactionId;
            LogService.logTrace(url);
            await new Promise((resolve) => setTimeout(resolve, 5000));
            const res = await this.instance.get(url);
            let lastChildTransaction;
            if (res.data.transactions) {
                lastChildTransaction =
                    res.data.transactions[res.data.transactions.length - 1];
                LogService.logError(JSON.stringify(lastChildTransaction));
            }
            else {
                throw new Error('Response does not contain any transaction');
            }
            const result = {
                result: lastChildTransaction.result,
            };
            return result;
        }
        catch (error) {
            LogService.logError(error);
            return Promise.reject(new InvalidResponse(error));
        }
    }
    async accountToEvmAddress(accountId) {
        try {
            const accountInfoViewModel = await this.getAccountInfo(accountId);
            if (accountInfoViewModel.accountEvmAddress) {
                return new EvmAddress(accountInfoViewModel.accountEvmAddress);
            }
            else if (accountInfoViewModel.publicKey) {
                return this.getAccountEvmAddressFromPrivateKeyType(accountInfoViewModel.publicKey.type, accountInfoViewModel.publicKey.key, accountId);
            }
            else {
                return Promise.reject('');
            }
        }
        catch (e) {
            throw new Error('EVM address could not be retrieved for ' +
                accountId.toString() +
                ' error : ' +
                e);
        }
    }
    async getAccountEvmAddressFromPrivateKeyType(privateKeyType, publicKey, accountId) {
        switch (privateKeyType) {
            case KeyType.ECDSA:
                return new EvmAddress(HPublicKey.fromString(publicKey).toEthereumAddress());
            default:
                return new EvmAddress('0x' +
                    (await this.getContractInfo(accountId.toHederaAddress().toString())).evmAddress);
        }
    }
    async getHBARBalance(accountId) {
        try {
            const url = `${this.mirrorNodeConfig.baseUrl}balances?account.id=${accountId.toString()}`;
            LogService.logTrace(url);
            const res = await this.instance.get(url);
            if (!res.data.balances)
                throw new Error('Response does not contain a balances result');
            return BigDecimal.fromString(res.data.balances[res.data.balances.length - 1].balance.toString());
        }
        catch (error) {
            LogService.logError(error);
            return Promise.reject(new InvalidResponse(error));
        }
    }
};
MirrorNodeAdapter = __decorate([
    singleton()
], MirrorNodeAdapter);
export { MirrorNodeAdapter };
